import { Promotion } from "./types"
import { lines_for_save } from "./deal_math"

/**
 * The kind of deal a promotion type is, which decides what the form asks for.
 *
 * Promotion types are free text (each org has its own list, and imports bring spellings like
 * "OI + MCB + Ad" or "Contra Revenue - Slotting Fees"), so the category is read from the words in
 * the type rather than from a fixed list. `promotion_type_layout` is the single rule: the create
 * form, the edit forms, validation and the save-time cleanup all go through it.
 */
export type PromotionCategory = "price" | "scan" | "display" | "fee" | "slotting" | "accrual"

/** Display order of the categories in the type dropdown. */
export const PROMOTION_CATEGORY_ORDER: PromotionCategory[] = ["price", "scan", "display", "fee", "slotting", "accrual"]

export const PROMOTION_CATEGORY_LABELS: Record<PromotionCategory, string> = {
  price: "Price promotion",
  scan: "Scan or chargeback",
  display: "Display",
  fee: "Fee or flat amount",
  slotting: "Slotting or rebate",
  accrual: "Accrual or other",
}

/** Which deal-term inputs a type takes. */
export type DealFields = "discount" | "multibuy" | "bogo" | "none"

export interface PromotionTypeLayout {
  category: PromotionCategory
  deal_fields: DealFields
  /** The discount inputs are shown but a value is not expected (displays, unrecognised types). */
  discount_optional: boolean
  /** Planned volume, expected lift and store participation apply to this type. */
  show_volume: boolean
  /** A per-unit rate × units estimate makes sense (the Expected trade box and its example). */
  per_unit: boolean
  /** The deal can sit at the retailer level, not on specific items. */
  products_optional: boolean
}

// Word patterns, checked against the type in this order. A type that names any per-unit tactic
// ("OI + MCB + Ad", "Display + MCB") is a rate deal even when it also names a fee.
const SCAN_PATTERN = /\b(scans?|mcb|chargebacks?|bill ?backs?|bb)\b/i
const PRICE_PATTERN =
  /\b(tpr|tpc|oi|off[- ]?invoice|edlp|edlc|edv|coupons?|bogo|multi-?buy|price|discounts?|srp|spda)\b|\d+\s*\/\s*\$|\$\s*\d|\d+\s*%/i
const DISPLAY_PATTERN = /\bdisplays?\b/i
const SLOTTING_PATTERN = /\b(slotting|contra revenue|rebates?|free fill)\b/i
const FEE_PATTERN =
  /\b(fees?|ads?|advertising|signage|marketing|program|demos?|flyers?|digital|samples?|food show|shopper|mkt)\b/i
const ACCRUAL_PATTERN = /\b(accruals?|other)\b/i

/** PURE: the category a promotion type belongs to. Unrecognised types are "Accrual or other". */
export function promotion_category(promotion_type: string | undefined): PromotionCategory {
  const type = (promotion_type ?? "").trim()
  if (!type) return "accrual"
  if (SCAN_PATTERN.test(type)) return "scan"
  if (PRICE_PATTERN.test(type)) return "price"
  if (DISPLAY_PATTERN.test(type)) return "display"
  if (SLOTTING_PATTERN.test(type)) return "slotting"
  if (FEE_PATTERN.test(type)) return "fee"
  return "accrual"
}

function is_exact_type(promotion_type: string, ...names: string[]): boolean {
  const normalized = promotion_type.trim().toLowerCase()
  return names.some((name: string) => name.toLowerCase() === normalized)
}

/** PURE: what the promotion form shows for a promotion type. */
export function promotion_type_layout(promotion_type: string | undefined): PromotionTypeLayout {
  const type = (promotion_type ?? "").trim()
  const category = promotion_category(type)

  // Multi-Buy and BOGO keep the special handling they always had, matched on their exact names so
  // imported variants ("Multibuy", "BOGO $3.25") keep the discount fields they were saved with.
  if (is_exact_type(type, "Multi-Buy")) {
    return { category, deal_fields: "multibuy", discount_optional: false, show_volume: true, per_unit: false, products_optional: false }
  }
  if (is_exact_type(type, "BOGO")) {
    return { category, deal_fields: "bogo", discount_optional: false, show_volume: true, per_unit: false, products_optional: false }
  }

  switch (category) {
    case "price":
    case "scan":
      return { category, deal_fields: "discount", discount_optional: false, show_volume: true, per_unit: true, products_optional: false }
    case "display":
      // A display is usually a fee, sometimes with a unit discount: offer both, ask for neither.
      return { category, deal_fields: "discount", discount_optional: true, show_volume: true, per_unit: true, products_optional: false }
    case "fee":
      return { category, deal_fields: "none", discount_optional: false, show_volume: false, per_unit: false, products_optional: true }
    case "slotting":
      return { category, deal_fields: "none", discount_optional: false, show_volume: false, per_unit: false, products_optional: false }
    case "accrual":
      // "Non-Working Accrual" and "Other" are lump sums. A type no rule recognises is filed here
      // too, but it may be anything, so it keeps the discount inputs (optional) rather than hiding
      // a rate it might carry.
      if (!type || ACCRUAL_PATTERN.test(type)) {
        return { category, deal_fields: "none", discount_optional: false, show_volume: false, per_unit: false, products_optional: true }
      }
      return { category, deal_fields: "discount", discount_optional: true, show_volume: true, per_unit: true, products_optional: false }
  }
}

/**
 * PURE: the promotion as it should be saved for its type — the deal fields, volume and
 * assumptions that do not apply to it are dropped. The form keeps them while the rep is editing
 * (so switching type and back loses nothing); only the save leaves them behind.
 */
export function strip_fields_for_type(promotion: Promotion): Promotion {
  const layout = promotion_type_layout(promotion.promotion_type)
  const next: Promotion = { ...promotion, lines: lines_for_save(promotion) }
  if (layout.deal_fields !== "discount") {
    next.discount_amount = undefined
    next.discount_percentage = undefined
  }
  if (layout.deal_fields !== "multibuy") {
    next.multibuy_quantity = undefined
    next.multibuy_price = undefined
  }
  if (layout.deal_fields !== "bogo") {
    next.bogo_buy_quantity = undefined
    next.bogo_get_quantity = undefined
    next.bogo_free_unit_cost = undefined
  }
  if (layout.deal_fields === "none") {
    // A fee has no unit price to take a deal off, and no per-product deal lines. Sending an empty
    // list (not undefined) removes lines saved under an earlier type.
    next.regular_price = undefined
    if (next.lines && next.lines.length > 0) next.lines = []
  }
  if (!layout.show_volume) {
    next.planned_volume_cases = undefined
    next.planned_volume_units = undefined
    next.expected_lift_pct = undefined
    next.participation_pct = undefined
  }
  return next
}

/** PURE: whether a type's deal is priced per unit, so a regular price and per-product lines apply. */
export function has_unit_deal(layout: PromotionTypeLayout): boolean {
  return layout.deal_fields !== "none"
}

/**
 * PURE: the layout for editing a promotion that is already saved under its type. Imports carry
 * discounts and volumes on types this form would not ask for them (an "Other" with a $ rate), and
 * hiding a saved value would leave the rep unable to see or fix it — so whatever is already filled
 * stays on screen until the type is changed.
 */
export function layout_for_saved_promotion(promotion: Promotion): PromotionTypeLayout {
  const layout = promotion_type_layout(promotion.promotion_type)
  const has_discount = promotion.discount_amount != null || promotion.discount_percentage != null
  const has_bogo = promotion.bogo_buy_quantity != null || promotion.bogo_get_quantity != null
  const has_volume =
    promotion.planned_volume_units != null ||
    promotion.planned_volume_cases != null ||
    promotion.expected_lift_pct != null ||
    promotion.participation_pct != null
  return {
    ...layout,
    ...(layout.deal_fields === "none" && has_discount ? { deal_fields: "discount" as const, discount_optional: true } : {}),
    ...(layout.deal_fields === "none" && !has_discount && has_bogo ? { deal_fields: "bogo" as const } : {}),
    ...(!layout.show_volume && has_volume ? { show_volume: true } : {}),
  }
}

/** Option for a type dropdown, carrying the category heading it is listed under. */
export interface GroupedTypeOption {
  value: string
  label: string
  group: string
}

/** PURE: type options grouped by category (in `PROMOTION_CATEGORY_ORDER`), alphabetical within. */
export function group_promotion_type_options(options: Array<{ value: string; label: string }>): GroupedTypeOption[] {
  return options
    .map((option: { value: string; label: string }) => ({
      ...option,
      category: promotion_category(option.value),
    }))
    .sort(
      (a, b) =>
        PROMOTION_CATEGORY_ORDER.indexOf(a.category) - PROMOTION_CATEGORY_ORDER.indexOf(b.category) ||
        a.label.localeCompare(b.label),
    )
    .map(({ category, ...option }) => ({ ...option, group: PROMOTION_CATEGORY_LABELS[category] }))
}

/** PURE: the Planned Amount help text for a type. */
export function planned_amount_help(layout: PromotionTypeLayout): string {
  if (layout.per_unit) {
    return "Total spend for the whole promotion — what you expect to be deducted overall. Example: $1 off × 5,000 units = $5,000."
  }
  if (layout.deal_fields === "multibuy" || layout.show_volume) {
    return "Total spend for the whole promotion — what you expect to be deducted overall."
  }
  return "The whole cost of this deal — the fee, slotting or accrual amount you expect the customer to deduct."
}

/** PURE: the Start Date help text for a type. */
export function start_date_help(layout: PromotionTypeLayout): string {
  if (layout.category === "slotting" || layout.category === "accrual" || layout.category === "fee") {
    return "Optional. For an annual rebate or an ongoing charge, use the period it covers — deductions are matched against these dates."
  }
  return "When the promotion runs at retail — deductions are matched against these dates."
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("promotion_category", () => {
    it("files Bakhrom's list the way his table does", () => {
      const expected: Array<[string, PromotionCategory]> = [
        ["TPR", "price"],
        ["Coupon", "price"],
        ["EDLP", "price"],
        ["OI", "price"],
        ["BOGO", "price"],
        ["Multi-Buy", "price"],
        ["Scan", "scan"],
        ["APP - Digital Scan", "scan"],
        ["MCB", "scan"],
        ["Display", "display"],
        ["On Display", "display"],
        ["AD Fee - Signage", "fee"],
        ["Advertising - Digital Fee", "fee"],
        ["Program Fee", "fee"],
        ["Shopper Marketing", "fee"],
        ["Slotting", "slotting"],
        ["Contra Revenue - Slotting Fees", "slotting"],
        ["Contra Revenue - Rebates", "slotting"],
        ["Non-Working Accrual", "accrual"],
        ["Other", "accrual"],
      ]
      for (const [type, category] of expected) {
        expect([type, promotion_category(type)]).toEqual([type, category])
      }
    })

    it("treats any per-unit tactic in a combined type as a rate deal", () => {
      expect(promotion_category("OI + MCB + Ad")).toBe("scan")
      expect(promotion_category("Display + MCB")).toBe("scan")
      expect(promotion_category("TPR Fee")).toBe("price")
      expect(promotion_category("SRP 2/$6")).toBe("price")
      expect(promotion_category("15% OI")).toBe("price")
    })

    it("files unknown and blank types under Accrual or other", () => {
      expect(promotion_category("VIP")).toBe("accrual")
      expect(promotion_category("")).toBe("accrual")
      expect(promotion_category(undefined)).toBe("accrual")
    })
  })

  describe("promotion_type_layout", () => {
    it("shows discount and volume only for price and scan types", () => {
      for (const type of ["TPR", "Scan", "MCB", "OI", "Coupon", "EDLP", "APP - Digital Scan"]) {
        const layout = promotion_type_layout(type)
        expect([type, layout.deal_fields, layout.show_volume, layout.per_unit]).toEqual([type, "discount", true, true])
      }
    })

    it("asks fees, slotting and accruals for Planned Amount only", () => {
      for (const type of ["AD Fee - Signage", "Program Fee", "Advertising - Digital Fee", "Shopper Marketing", "Slotting", "Contra Revenue - Slotting Fees", "Contra Revenue - Rebates", "Non-Working Accrual", "Other"]) {
        const layout = promotion_type_layout(type)
        expect([type, layout.deal_fields, layout.show_volume, layout.per_unit]).toEqual([type, "none", false, false])
      }
    })

    it("lays out Slotting and Contra Revenue - Slotting Fees the same", () => {
      expect(promotion_type_layout("Contra Revenue - Slotting Fees")).toEqual(promotion_type_layout("Slotting"))
    })

    it("makes the discount optional on a display", () => {
      expect(promotion_type_layout("Display")).toMatchObject({ deal_fields: "discount", discount_optional: true, show_volume: true })
    })

    it("keeps Multi-Buy and BOGO special", () => {
      expect(promotion_type_layout("Multi-Buy")).toMatchObject({ deal_fields: "multibuy", show_volume: true, per_unit: false })
      expect(promotion_type_layout("BOGO")).toMatchObject({ deal_fields: "bogo", show_volume: true, per_unit: false })
      // Imported spellings keep the discount fields they were saved with.
      expect(promotion_type_layout("Multibuy").deal_fields).toBe("discount")
      expect(promotion_type_layout("BOGO $3.25").deal_fields).toBe("discount")
    })

    it("lets fee and accrual deals go without products", () => {
      expect(promotion_type_layout("AD Fee - Signage").products_optional).toBe(true)
      expect(promotion_type_layout("Non-Working Accrual").products_optional).toBe(true)
      expect(promotion_type_layout("TPR").products_optional).toBe(false)
      expect(promotion_type_layout("Slotting").products_optional).toBe(false)
    })

    it("keeps optional discount inputs on a type it does not recognise", () => {
      expect(promotion_type_layout("VIP")).toMatchObject({ category: "accrual", deal_fields: "discount", discount_optional: true })
    })
  })

  describe("strip_fields_for_type", () => {
    const entered: Promotion = {
      name: "Signage",
      products: [],
      promotion_type: "AD Fee - Signage",
      discount_amount: 1.5,
      discount_percentage: 15,
      multibuy_quantity: 2,
      multibuy_price: 6,
      planned_volume_units: 10_000,
      planned_volume_cases: 500,
      expected_lift_pct: 20,
      participation_pct: 50,
      planned_amount: 2_500,
    }

    it("saves only Planned Amount on a flat fee", () => {
      const saved = strip_fields_for_type(entered)
      expect(saved.planned_amount).toBe(2_500)
      expect(saved.discount_amount).toBeUndefined()
      expect(saved.discount_percentage).toBeUndefined()
      expect(saved.multibuy_quantity).toBeUndefined()
      expect(saved.planned_volume_units).toBeUndefined()
      expect(saved.expected_lift_pct).toBeUndefined()
      expect(saved.participation_pct).toBeUndefined()
    })

    it("keeps the discount and volume on a TPR, dropping the multi-buy terms", () => {
      const saved = strip_fields_for_type({ ...entered, promotion_type: "TPR" })
      expect(saved.discount_amount).toBe(1.5)
      expect(saved.discount_percentage).toBe(15)
      expect(saved.planned_volume_units).toBe(10_000)
      expect(saved.participation_pct).toBe(50)
      expect(saved.multibuy_quantity).toBeUndefined()
    })

    it("keeps the multi-buy terms on a Multi-Buy, dropping the discount", () => {
      const saved = strip_fields_for_type({ ...entered, promotion_type: "Multi-Buy" })
      expect(saved.multibuy_quantity).toBe(2)
      expect(saved.multibuy_price).toBe(6)
      expect(saved.discount_amount).toBeUndefined()
      expect(saved.planned_volume_units).toBe(10_000)
    })

    it("does not change the promotion it was given", () => {
      strip_fields_for_type(entered)
      expect(entered.discount_amount).toBe(1.5)
    })

    it("keeps BOGO terms only on a BOGO, and drops the regular price and lines from a fee", () => {
      const bogo: Promotion = { ...entered, promotion_type: "BOGO", bogo_buy_quantity: 1, bogo_get_quantity: 1, regular_price: 3.99, lines: [{ product_name: "x" }] }
      const saved = strip_fields_for_type(bogo)
      expect(saved.bogo_buy_quantity).toBe(1)
      expect(saved.regular_price).toBe(3.99)
      expect(saved.lines).toHaveLength(1)
      expect(saved.discount_amount).toBeUndefined()
      const fee = strip_fields_for_type({ ...bogo, promotion_type: "AD Fee - Signage" })
      expect(fee.bogo_buy_quantity).toBeUndefined()
      expect(fee.regular_price).toBeUndefined()
      // An empty list, not undefined: the save must remove lines filed under the old type.
      expect(fee.lines).toEqual([])
      // Nothing saved, nothing to remove.
      expect(strip_fields_for_type({ ...entered, lines: undefined }).lines).toBeUndefined()
    })
  })

  describe("layout_for_saved_promotion", () => {
    it("keeps showing a saved discount and volume on a type that would hide them", () => {
      const layout = layout_for_saved_promotion({ name: "x", products: [], promotion_type: "Other", discount_amount: 0.5, planned_volume_units: 100 })
      expect(layout).toMatchObject({ deal_fields: "discount", discount_optional: true, show_volume: true })
    })

    it("hides what is empty", () => {
      expect(layout_for_saved_promotion({ name: "x", products: [], promotion_type: "Other" })).toEqual(promotion_type_layout("Other"))
    })
  })

  describe("group_promotion_type_options", () => {
    it("orders by category, then label, and names each group", () => {
      const grouped = group_promotion_type_options([
        { value: "Slotting", label: "Slotting" },
        { value: "TPR", label: "TPR" },
        { value: "Other", label: "Other" },
        { value: "Scan", label: "Scan" },
        { value: "Coupon", label: "Coupon" },
      ])
      expect(grouped.map((option: GroupedTypeOption) => [option.value, option.group])).toEqual([
        ["Coupon", "Price promotion"],
        ["TPR", "Price promotion"],
        ["Scan", "Scan or chargeback"],
        ["Slotting", "Slotting or rebate"],
        ["Other", "Accrual or other"],
      ])
    })
  })

  describe("help text", () => {
    it("shows the per-unit example only for per-unit types", () => {
      expect(planned_amount_help(promotion_type_layout("TPR"))).toContain("$1 off × 5,000 units")
      expect(planned_amount_help(promotion_type_layout("Slotting"))).not.toContain("units")
      expect(planned_amount_help(promotion_type_layout("AD Fee - Signage"))).not.toContain("units")
    })
  })
}
